import WatermarkImage from "assets/images/watermark-logo.png";
import Button from "components/common/Button";
import useMusings from "components/useMusings";
import { FC, useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { RoutesEnum } from "utils/Routes";
import Hls from "hls.js";

const MusingsDiv = styled.div`
  margin-bottom: 150px;

  header {
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: 1740px;
    margin: 0 auto;
    padding: 24px 16px;
    @media (min-width: 768px) {
      padding: 47px 64px 47px 76px;
    }
  }

  .hype-preview {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 48px;

    h2 {
      max-width: 480px;
      padding: 0 16px;
      font-size: clamp(22px, 5vw, 32px);
      font-weight: 400;
      text-align: center;
    }

    .video-preview {
      position: relative;
      /* Shrink-wrap the video so a portrait clip doesn't sit in a wide black box (was width:100% +
         a fixed max-width, which pillarboxed vertical videos). fit-content + the video sized by its
         own aspect (height-capped) means the frame hugs the video for portrait OR landscape. */
      width: fit-content;
      max-width: min(600px, 100%);
      margin: 20px auto 50px;
      border-radius: 8px;
      overflow: hidden;
      background: #000;
      box-shadow: rgba(0, 0, 0, 0.7) 0px 18px 50px -10px;

      video {
        display: block;
        max-width: 100%;
        max-height: 80vh;
        width: auto;
        height: auto;
        object-fit: contain;
        background: #000;
      }

      .watermark {
        position: absolute;
        bottom: 12px;
        right: 12px;
        max-width: 22%;
        pointer-events: none;
      }
    }

    .share {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
      justify-content: center;
      padding: 0 16px;
    }
  }
`;

const MusingsPage: FC = () => {
  const { item, loading } = useMusings();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  const attachPlayer = useCallback((src: string, video: HTMLVideoElement) => {
    // Tear down any previous instance.
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    // Prefer hls.js where MSE is available — covers desktop Chrome / Firefox /
    // Edge / Safari and Android Chrome. We can't trust canPlayType for HLS
    // because Chrome returns "maybe" without actually decoding it.
    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(src);
      hls.attachMedia(video);
      hlsRef.current = hls;
      return;
    }
    // iOS Safari has no MSE — use the built-in native HLS path.
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.load();
    }
  }, []);

  // Callback ref: fires the moment the <video> element mounts. This avoids
  // race conditions from the early-return pattern where useRef + useEffect
  // could miss attachment if React mounts the element after the effect's
  // first scheduled run.
  const setVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      videoRef.current = el;
      if (el && item?.linkToAsset) {
        attachPlayer(item.linkToAsset, el);
      }
    },
    [item?.linkToAsset, attachPlayer],
  );

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, []);

  if (loading || !item) return null;

  return (
    <MusingsDiv>
      <header>
        <Link to={RoutesEnum.home}>
          <img src="/icons/776-logo.svg" alt="776" />
        </Link>

        <a href="https://776.org" target="_blank" rel="noreferrer">
          <img src="/icons/heart.svg" alt="" width={26} height={26} />
        </a>
      </header>

      <div className="hype-preview">
        <h2>{item?.recorderName} recorded this for you with Cerebro!</h2>

        <div className="video-preview">
          <video
            ref={setVideoRef}
            controls
            playsInline
            poster={item?.linkToAssetCoverImage}
          />
          {(item?.watermark ?? true) && (
            <img className="watermark" src={WatermarkImage} alt="watermark" />
          )}
        </div>

        <div className="share">
          <a
            href={`https://twitter.com/intent/tweet?url=${item?.linkToPage}`}
            target="_blank"
            rel="noreferrer"
          >
            <Button dark>Share on X</Button>
          </a>
          <a href={item?.linkToDownload} target="_blank" rel="noreferrer">
            <Button dark>Download</Button>
          </a>
        </div>
      </div>
    </MusingsDiv>
  );
};

export default MusingsPage;
